import type {
  Todo,
  TodoIdMap,
  TodoUpdateDelta,
  TodoUpdateDeltaIdMap,
} from "@common/types/todo";
import TodoUtils from "@common/utils/todo-utils";

export type UndoAction = {
  from: TodoUpdateDeltaIdMap;
  to: TodoUpdateDeltaIdMap;
};

export function makeUndoAction(
  oldTodoIdMap: TodoIdMap,
  toTodoIdMap: TodoUpdateDeltaIdMap
): UndoAction {
  const fromDeltaIdMap: TodoUpdateDeltaIdMap = {};

  for (let [todoId, todoDelta] of Object.entries(toTodoIdMap)) {
    const oldFullTodo = oldTodoIdMap[todoId];
    if (!oldFullTodo) {
      const partialTodo: Partial<Todo> = todoDelta;
      const isJustCreated = TodoUtils.isFullTodo(partialTodo);
      if (isJustCreated) {
        // the state to revert to is deleting the todo
        fromDeltaIdMap[partialTodo.id] = { id: todoDelta.id, deleted: true };
        continue;
      } else {
        // without the old todo, we can't keep a snapshot of the previous state
        console.warn("no 'from' todo", todoDelta);
        continue;
      }
    }

    // build a minimal snapshot of the old value (payload)
    const fromTodoDelta: TodoUpdateDelta = { id: todoId };

    // iterate through properties of todoDelta, access the property from oldFullTodo
    // and add it to oldTodoPayload
    for (let key of Object.keys(todoDelta)) {
      const todoKey = key as keyof TodoUpdateDelta;
      if (oldFullTodo[todoKey] !== undefined) {
        (fromTodoDelta[todoKey] as Partial<
          TodoUpdateDelta[keyof TodoUpdateDelta]
        >) = oldFullTodo[todoKey];
      }
    }

    fromDeltaIdMap[todoId] = fromTodoDelta;
  }
  return {
    from: fromDeltaIdMap,
    to: toTodoIdMap,
  };
}

/**
 * for small granular actions like updating the value of a todo
 * we want to batch it together
 */
export function reduceUndoStack(undoStack: UndoAction[]): UndoAction[] {
  return undoStack.reduce((accActions, curAction) => {
    const newAcc = accActions;
    const lastAction = newAcc[newAcc.length - 1];
    if (!lastAction) {
      return [curAction];
    }
    // if the last action is the same kind as current action
    let newCurAction = curAction;
    const fromEntries = Object.entries(lastAction.from);
    const fromId = fromEntries[0]?.[0];
    const fromTodo = fromEntries[0]?.[1];
    const toEntries = Object.entries(curAction.to);
    const toId = toEntries[0]?.[0];
    const toTodo = toEntries[0]?.[1];

    if (!fromTodo || !toTodo) {
      // skip this undo because it is an empty action
      console.warn("empty action", curAction);
      return accActions;
    }

    const onlyUpdatesOneTodo = fromEntries.length == 1 && toEntries.length == 1;
    const updatesSameTodo = fromId == toId;

    // We don't want to batnch movemenet commands as they are pretty intentional
    const ignoreBatchProperties: (keyof Todo)[] = ["date", "lexidx"];
    const updatesSameProperties =
      Object.keys(fromTodo).every(
        (key) =>
          key in toTodo && !ignoreBatchProperties.includes(key as keyof Todo)
      ) && Object.keys(toTodo).length == Object.keys(fromTodo).length;

    let shouldBatchWithLast =
      onlyUpdatesOneTodo && updatesSameTodo && updatesSameProperties;

    if (shouldBatchWithLast) {
      newAcc.pop(); // remove last
      newCurAction = {
        // and merge
        from: lastAction.from,
        to: curAction.to,
      };
    }

    newAcc.push(newCurAction);

    return newAcc;
  }, [] as UndoAction[]);
}
